import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollReveal from '../../components/ui/ScrollReveal';
import { useState } from 'react';
import { useSEO } from '../../hooks/useSEO';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', goals: '', source: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useSEO({
    title: 'Book a Free Consultation | Laura Martin Fitness London',
    description:
      'Ready to transform your body? Book a free no-obligation consultation with London PT Laura Martin. In-person & online coaching available.',
  });

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email is required';
    if (!form.goals.trim()) e.goals = 'Please tell Laura about your goals';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitted(true);
  };

  const inputCls = (field: string) =>
    `w-full bg-stone-50 border ${errors[field] ? 'border-red-400' : 'border-stone-200'} rounded-xl px-5 py-4 text-stone-900 font-sans text-sm placeholder:text-stone-400 focus:outline-none focus:border-[#c9a96e] focus:bg-white transition-all duration-200`;

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] lg:h-[70vh] lg:min-h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img src="/images/Laura_3_edited.jpg" alt="Book consultation with Laura Martin personal trainer London" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917]/80 via-[#1c1917]/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #faf8f5 0%, rgba(250,248,245,0.35) 18%, transparent 42%)' }} />
          </div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36">
            <div className="absolute bottom-0 pb-10 lg:pb-24 max-w-3xl">
              <div className="flex items-center gap-4 mb-5">
                <span className="w-8 h-px bg-[#c9a96e]" />
                <span className="text-[#c9a96e] text-[10px] font-sans font-semibold tracking-[0.32em] uppercase">Get In Touch</span>
              </div>
              <h1 className="text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.6rem, 5.5vw, 4.5rem)', fontWeight: 600, lineHeight: 1.0 }}>
                Let's Start
                <br />
                <em className="text-[#c9a96e] not-italic">Your Journey.</em>
              </h1>
              <p className="text-white/70 font-sans text-base max-w-md">
                Fill in the form and Laura will be in touch within 24 hours to arrange your free no-obligation consultation.
              </p>
            </div>
          </div>
        </section>

        {/* Form + info */}
        <section className="bg-[#faf8f5] py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

              {/* Form */}
              <ScrollReveal className="lg:col-span-3">
                {submitted ? (
                  <div className="bg-white rounded-2xl p-12 text-center border border-stone-200 shadow-sm">
                    <div className="w-16 h-16 rounded-full bg-[#c9a96e]/12 flex items-center justify-center mx-auto mb-6">
                      <i className="ri-check-line text-[#c9a96e] text-3xl" />
                    </div>
                    <h2 className="text-stone-900 text-3xl mb-3 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Message Received!</h2>
                    <p className="text-stone-500 font-sans text-sm leading-relaxed">Thank you for reaching out. Laura will be in touch within 24 hours to arrange your free consultation.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} name="contact" method="POST" data-netlify="true" className="bg-white rounded-2xl p-8 lg:p-10 border border-stone-200 shadow-sm">
                    <input type="hidden" name="form-name" value="contact" />
                    <h2 className="text-stone-900 text-2xl mb-8 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Book Your Free Consultation</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-stone-500 text-[10px] font-sans font-semibold tracking-[0.2em] uppercase mb-2">Full Name *</label>
                        <input type="text" name="name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls('name')} />
                        {errors.name && <p className="text-red-500 text-xs mt-1 font-sans">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-stone-500 text-[10px] font-sans font-semibold tracking-[0.2em] uppercase mb-2">Email Address *</label>
                        <input type="email" name="email" placeholder="your@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls('email')} />
                        {errors.email && <p className="text-red-500 text-xs mt-1 font-sans">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-stone-500 text-[10px] font-sans font-semibold tracking-[0.2em] uppercase mb-2">Phone (optional)</label>
                        <input type="tel" name="phone" placeholder="+44 7XXX XXXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls('phone')} />
                      </div>
                      <div>
                        <label className="block text-stone-500 text-[10px] font-sans font-semibold tracking-[0.2em] uppercase mb-2">Interested In</label>
                        <select name="service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={`${inputCls('service')} appearance-none`}>
                          <option value="">Select a service…</option>
                          <option>1-2-1 Personal Training</option>
                          <option>Bespoke Online Coaching</option>
                          <option>Nutritional Coaching</option>
                          <option>Combined Package</option>
                          <option>Not sure yet</option>
                        </select>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-stone-500 text-[10px] font-sans font-semibold tracking-[0.2em] uppercase mb-2">Your Goals *</label>
                      <textarea name="goals" rows={5} placeholder="Tell Laura a bit about yourself and what you'd like to achieve…" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} className={`${inputCls('goals')} resize-none`} />
                      {errors.goals && <p className="text-red-500 text-xs mt-1 font-sans">{errors.goals}</p>}
                    </div>

                    <div className="mb-8">
                      <label className="block text-stone-500 text-[10px] font-sans font-semibold tracking-[0.2em] uppercase mb-2">How Did You Find Laura?</label>
                      <select name="source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={`${inputCls('source')} appearance-none`}>
                        <option value="">Select…</option>
                        <option>Instagram</option>
                        <option>Google Search</option>
                        <option>Referred by a friend</option>
                        <option>Other</option>
                      </select>
                    </div>

                    <button type="submit" className="w-full py-4 bg-[#c9a96e] text-white text-[10px] font-sans font-bold tracking-[0.3em] uppercase rounded-full hover:bg-[#a8863f] hover:shadow-xl hover:shadow-[#c9a96e]/20 transition-all duration-300">
                      Send Message
                    </button>
                  </form>
                )}
              </ScrollReveal>

              {/* Info */}
              <div className="lg:col-span-2 flex flex-col gap-5">
                <ScrollReveal delay={150}>
                  <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm">
                    <h3 className="text-stone-900 text-xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact Details</h3>
                    <div className="space-y-5">
                      <a href="mailto:lauramartinfitness@gmail.com" className="flex items-start gap-4 group">
                        <div className="w-9 h-9 rounded-full bg-[#c9a96e]/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-[#c9a96e] text-base" />
                        </div>
                        <div>
                          <p className="text-stone-400 text-[10px] font-sans tracking-widest uppercase mb-0.5">Email</p>
                          <p className="text-stone-700 font-sans text-sm group-hover:text-[#c9a96e] transition-colors">lauramartinfitness@gmail.com</p>
                        </div>
                      </a>
                      <a href="https://www.instagram.com/wee_lola_fit" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                        <div className="w-9 h-9 rounded-full bg-[#c9a96e]/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-instagram-line text-[#c9a96e] text-base" />
                        </div>
                        <div>
                          <p className="text-stone-400 text-[10px] font-sans tracking-widest uppercase mb-0.5">Instagram</p>
                          <p className="text-stone-700 font-sans text-sm group-hover:text-[#c9a96e] transition-colors">@wee_lola_fit</p>
                        </div>
                      </a>
                      <div className="flex items-start gap-4">
                        <div className="w-9 h-9 rounded-full bg-[#c9a96e]/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line text-[#c9a96e] text-base" />
                        </div>
                        <div>
                          <p className="text-stone-400 text-[10px] font-sans tracking-widest uppercase mb-0.5">Location</p>
                          <p className="text-stone-700 font-sans text-sm">London, United Kingdom</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>

                <ScrollReveal delay={220}>
                  <div className="bg-[#c9a96e]/8 rounded-2xl p-8 border border-[#c9a96e]/20">
                    <i className="ri-chat-smile-2-line text-[#c9a96e] text-2xl mb-4 block" />
                    <h4 className="text-stone-900 text-lg mb-2 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Free Consultation</h4>
                    <p className="text-stone-600 font-sans text-sm leading-relaxed">
                      Every new client begins with a free 30-minute consultation — no pressure, no obligation. Just a genuine conversation about your goals.
                    </p>
                  </div>
                </ScrollReveal>

                {/* Fixed contact image — portrait of Laura not hands */}
                <ScrollReveal delay={290}>
                  <div className="relative h-52 rounded-2xl overflow-hidden shadow-sm">
                    <img
                      src="/images/Laura_3_edited.jpg"
                      alt="Laura Martin personal trainer London"
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent" />
                    <div className="absolute bottom-5 left-5">
                      <p className="text-white font-semibold text-lg leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Laura Martin</p>
                      <p className="text-white/70 text-xs font-sans tracking-wide">Personal Trainer · London</p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
